import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PatientDialog from "@/components/PatientDialog";
import AppointmentDialog from "@/components/AppointmentDialog";
import ConsultationDialog from "@/components/ConsultationDialog";
import { InvoiceDialog, PaymentDialog } from "@/components/BillingDialogs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CaretLeft, PencilSimple, CalendarPlus, Stethoscope, Receipt, Pill, DownloadSimple, Money, Key, LinkSimple } from "@phosphor-icons/react";

const STATUS_CLS = {
  scheduled: "bg-primary/10 text-primary border-primary/30",
  completed: "bg-success/10 text-success border-success/40",
  cancelled: "bg-muted text-muted-foreground border-border",
  no_show: "bg-destructive/10 text-destructive border-destructive/40",
  paid: "bg-success/10 text-success border-success/40",
  partial: "bg-warning/10 text-warning border-warning/40",
  unpaid: "bg-destructive/10 text-destructive border-destructive/40",
};
const Badge = ({ s }) => <span className={`inline-flex px-2 py-0.5 rounded-sm border text-xs font-medium capitalize ${STATUS_CLS[s] || "border-border"}`}>{String(s).replace("_", " ")}</span>;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");

async function downloadInvoice(inv) {
  try {
    const { data } = await api.get(`/clinic/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `${inv.invoice_no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Could not download invoice"); }
}

function Info({ label, value }) {
  return <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p><p className="text-sm mt-0.5">{value || "—"}</p></div>;
}

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, money } = useAuth();
  const [data, setData] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [edit, setEdit] = useState(false);
  const [booking, setBooking] = useState(false);
  const [consultFor, setConsultFor] = useState(null);
  const [invoicing, setInvoicing] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);

  const can = (p) => user?.permissions?.includes(p);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/clinic/patients/${id}`); setData(data); }
    catch { toast.error("Patient not found"); navigate("/clinic/patients"); }
  }, [id, navigate]);

  useEffect(() => {
    load();
    api.get("/clinic/doctors").then(({ data }) => setDoctors(data)).catch(() => {});
    api.get("/clinic/meta").then(({ data }) => { setDeviceTypes(data.device_types); setMethods(data.payment_methods); }).catch(() => {});
  }, [load]);

  const setStatus = async (a, status) => {
    try { await api.patch(`/clinic/appointments/${a.id}`, { status }); toast.success("Appointment updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };
  const dispense = async (rx) => {
    try { await api.post(`/clinic/prescriptions/${rx.id}/dispense`); toast.success("Dispensed — stock updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  if (!data) return <div className="p-8"><div className="h-6 w-40 bg-muted rounded animate-pulse" /></div>;
  const p = data.patient;

  return (
    <div data-testid="patient-detail-page">
      <div className="border-b border-border px-6 md:px-8 py-6 bg-card">
        <button onClick={() => navigate("/clinic/patients")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="back-to-patients"><CaretLeft size={14} /> Patients</button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight">{p.name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{p.patient_no}{p.phone ? ` · ${p.phone}` : ""}{data.outstanding > 0 ? <span className="text-destructive"> · Outstanding {money(data.outstanding)}</span> : ""}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {can("patient:write") && <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-patient-button" className="gap-1.5"><PencilSimple size={16} /> Edit</Button>}
            {can("appointment:write") && <Button variant="outline" onClick={() => setBooking(true)} data-testid="book-appointment-button" className="gap-1.5"><CalendarPlus size={16} /> Book</Button>}
            {can("invoice:write") && <Button onClick={() => setInvoicing(true)} data-testid="new-invoice-button" className="bg-primary hover:bg-primary/90 gap-1.5"><Receipt size={16} /> Invoice</Button>}
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8">
        <Tabs defaultValue="overview">
          <TabsList data-testid="patient-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">Appointments ({data.appointments.length})</TabsTrigger>
            <TabsTrigger value="consultations" data-testid="tab-consultations">Consultations ({data.consultations.length})</TabsTrigger>
            <TabsTrigger value="prescriptions" data-testid="tab-prescriptions">Prescriptions ({data.prescriptions.length})</TabsTrigger>
            <TabsTrigger value="billing" data-testid="tab-billing">Billing ({data.invoices.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-5 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-card border border-border rounded-md p-5 grid grid-cols-2 gap-4">
                <div className="col-span-2 text-[11px] uppercase tracking-[0.15em] text-primary font-semibold">Patient information</div>
                <Info label="Gender" value={p.gender} /><Info label="Age" value={p.age} />
                <Info label="Email" value={p.email} /><Info label="Occupation" value={p.occupation} />
                <Info label="Address" value={p.address} /><Info label="Blood group" value={p.blood_group} />
              </div>
              <div className="bg-card border border-border rounded-md p-5 grid grid-cols-2 gap-4">
                <div className="col-span-2 text-[11px] uppercase tracking-[0.15em] text-primary font-semibold">Clinical & assessment</div>
                <Info label="Cause of amputation" value={p.cause_of_amputation} /><Info label="Date of amputation" value={p.date_of_amputation && fmtDate(p.date_of_amputation)} />
                <Info label="Device(s) required" value={(p.device_types || []).join(", ")} /><Info label="Product name" value={p.product_name} />
                <Info label="Referring doctor" value={p.referring_doctor} /><Info label="Hospital" value={p.hospital_name} />
                <div className="col-span-2"><Info label="Patient history" value={p.patient_history} /></div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-md p-5">
              <p className="text-[11px] uppercase tracking-[0.15em] text-primary font-semibold mb-3">Patient portal login</p>
              {data.login ? (
                <p className="text-sm text-success flex items-center gap-2"><Key size={15} /> Portal enabled for <span className="font-medium">{data.login.email}</span></p>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm text-muted-foreground">No portal login yet.</p>
                  {can("patient:write") && <Button size="sm" variant="outline" onClick={() => setLoginOpen(true)} data-testid="portal-login-button" className="gap-1.5"><Key size={14} /> Enable portal login</Button>}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="appointments" className="mt-5">
            <List empty="No appointments booked.">
              {data.appointments.map((a) => (
                <li key={a.id} data-testid={`appt-${a.id}`} className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{fmtDate(a.date)} · {a.time} <span className="text-muted-foreground font-normal">with {a.doctor_name}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.reason || "General"}{a.follow_up_of ? " · Follow-up" : ""}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge s={a.status} />
                    {a.status === "scheduled" && can("consultation:write") && <Button size="sm" variant="outline" onClick={() => setConsultFor(a)} data-testid={`consult-${a.id}`} className="gap-1.5"><Stethoscope size={14} /> Consult</Button>}
                    {a.status === "scheduled" && can("appointment:write") && <Button size="sm" variant="ghost" onClick={() => setStatus(a, "cancelled")} data-testid={`cancel-${a.id}`} className="hover:text-destructive">Cancel</Button>}
                  </div>
                </li>
              ))}
            </List>
          </TabsContent>

          <TabsContent value="consultations" className="mt-5">
            <List empty="No consultations recorded.">
              {data.consultations.map((c) => (
                <li key={c.id} data-testid={`cons-${c.id}`} className="px-5 py-3.5">
                  <p className="text-sm font-medium">{fmtDate(c.created_at)} <span className="text-muted-foreground font-normal">· {c.doctor_name}</span></p>
                  {c.diagnosis && <p className="text-sm mt-1"><span className="text-muted-foreground">Diagnosis:</span> {c.diagnosis}</p>}
                  {c.symptoms && <p className="text-sm mt-0.5"><span className="text-muted-foreground">Symptoms:</span> {c.symptoms}</p>}
                  {c.notes && <p className="text-sm mt-0.5 whitespace-pre-wrap">{c.notes}</p>}
                  {c.follow_up_date && <p className="text-xs text-primary mt-1">Follow-up: {fmtDate(c.follow_up_date)}</p>}
                </li>
              ))}
            </List>
          </TabsContent>

          <TabsContent value="prescriptions" className="mt-5">
            <List empty="No prescriptions issued.">
              {data.prescriptions.map((rx) => {
                const canDispense = can("prescription:dispense") && rx.items.some((i) => i.inventory_item_id && !i.dispensed && i.quantity > 0);
                return (
                  <li key={rx.id} data-testid={`rx-${rx.id}`} className="px-5 py-3.5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium flex items-center gap-1.5"><Pill size={15} className="text-primary" /> {rx.rx_no} <span className="text-muted-foreground font-normal">· {rx.doctor_name} · {fmtDate(rx.created_at)}</span></p>
                      {canDispense && <Button size="sm" variant="outline" onClick={() => dispense(rx)} data-testid={`dispense-${rx.id}`}>Dispense from stock</Button>}
                    </div>
                    <ul className="mt-2 space-y-1">
                      {rx.items.map((i, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                          <span className="text-foreground">{i.name}</span>
                          {i.dosage && <span>· {i.dosage}</span>}{i.frequency && <span>· {i.frequency}</span>}{i.quantity > 0 && <span>· qty {i.quantity}</span>}
                          {i.inventory_item_id && (i.dispensed ? <span className="text-success text-xs">✓ dispensed</span> : <span className="text-warning text-xs">stock-linked</span>)}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </List>
          </TabsContent>

          <TabsContent value="billing" className="mt-5">
            <List empty="No invoices yet.">
              {data.invoices.map((inv) => (
                <li key={inv.id} data-testid={`inv-${inv.id}`} className="px-5 py-3.5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{inv.invoice_no} <span className="text-muted-foreground font-normal">· {fmtDate(inv.created_at)}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">Total {money(inv.total)} · Paid {money(inv.paid)} · Due {money(inv.balance)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge s={inv.status} />
                    {inv.balance > 0 && can("payment:write") && <Button size="sm" variant="outline" onClick={() => setPayFor(inv)} data-testid={`pay-${inv.id}`} className="gap-1.5"><Money size={14} /> Pay</Button>}
                    <Button size="sm" variant="ghost" onClick={() => downloadInvoice(inv)} data-testid={`pdf-${inv.id}`} className="gap-1.5"><DownloadSimple size={14} /> PDF</Button>
                  </div>
                </li>
              ))}
            </List>
          </TabsContent>
        </Tabs>
      </div>

      <PatientDialog open={edit} onOpenChange={setEdit} patient={p} deviceTypes={deviceTypes} onSaved={load} />
      <AppointmentDialog open={booking} onOpenChange={setBooking} doctors={doctors} lockedPatient={p} onSaved={load} />
      <ConsultationDialog open={Boolean(consultFor)} onOpenChange={(o) => !o && setConsultFor(null)} appointment={consultFor} onSaved={load} />
      <InvoiceDialog open={invoicing} onOpenChange={setInvoicing} patient={p} onSaved={load} />
      <PaymentDialog open={Boolean(payFor)} onOpenChange={(o) => !o && setPayFor(null)} invoice={payFor} methods={methods} onSaved={load} />
      <PortalLoginDialog open={loginOpen} onOpenChange={setLoginOpen} patient={p} onSaved={load} />
    </div>
  );
}

function List({ empty, children }) {
  const items = Array.isArray(children) ? children : [children];
  if (items.filter(Boolean).length === 0) return <div className="h-32 flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border rounded-md">{empty}</div>;
  return <div className="bg-card border border-border rounded-md overflow-hidden"><ul className="divide-y divide-border">{children}</ul></div>;
}

function PortalLoginDialog({ open, onOpenChange, patient, onSaved }) {
  const [mode, setMode] = useState("create");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMode("create"); setPassword(""); setEmail(patient?.email || ""); } }, [open, patient]);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "create") {
        if (password.length < 6) { toast.error("Password must be 6+ characters"); setBusy(false); return; }
        await api.post(`/clinic/patients/${patient.id}/login`, { password });
      } else {
        await api.post(`/clinic/patients/${patient.id}/link`, { email });
      }
      toast.success("Portal login set up");
      onOpenChange(false); onSaved?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="portal-login-dialog">
        <DialogHeader><DialogTitle className="font-heading">Patient portal login</DialogTitle></DialogHeader>
        <div className="flex gap-2 text-sm">
          <button onClick={() => setMode("create")} data-testid="portal-mode-create" className={`flex-1 py-1.5 rounded-md border ${mode === "create" ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>New login</button>
          <button onClick={() => setMode("link")} data-testid="portal-mode-link" className={`flex-1 py-1.5 rounded-md border ${mode === "link" ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>Link existing</button>
        </div>
        {mode === "create" ? (
          <div className="space-y-3 py-1">
            <p className="text-xs text-muted-foreground">Creates a login using the patient's email ({patient?.email || "add an email first"}).</p>
            <div className="space-y-1.5"><Label>Temporary password</Label><Input type="text" data-testid="portal-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6+ characters" /></div>
          </div>
        ) : (
          <div className="space-y-3 py-1">
            <p className="text-xs text-muted-foreground">Link an account the patient already registered with.</p>
            <div className="space-y-1.5"><Label>Patient's login email</Label><Input type="email" data-testid="portal-link-email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="portal-login-save" className="bg-primary hover:bg-primary/90 gap-1.5"><LinkSimple size={15} />{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
